import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check, Cog, Handshake, ShieldCheck, Users } from "lucide-react";
import Seo, { breadcrumbSchema, organizationSchema, websiteSchema } from "../components/Seo";
import { FadeIn, MaskedLines, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import CtaSection from "../components/CtaSection";
import ClientsGrid from "../components/ClientsGrid";
import { IndustryCard, ServiceCard } from "../components/Cards";
import { BENEFITS, INDUSTRIES, PROJECT_TYPES, SERVICES, SITE, WHY_POINTS } from "../data/site";

const ICONS = { ShieldCheck, Users, Cog, Handshake };
const img = (name) => `/assets/img/${name}-900.webp`;

function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative flex min-h-[92vh] items-center overflow-hidden bg-gig-black" data-testid="home-hero">
      <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
        <motion.div
          className="absolute inset-0"
          initial={reduce ? false : { scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div className="absolute inset-0" style={{ y: bgY }}>
            <img
              src="/assets/img/refinery-datacenter-hero.webp"
              alt=""
              className="ambient-pan absolute inset-0 h-full w-full object-cover object-[center_40%]"
              width="1920"
              height="1080"
              fetchPriority="high"
            />
          </motion.div>
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-gig-black/90 via-gig-black/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-gig-black/90 via-transparent to-gig-black/40" />
      </div>

      <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-[1440px] px-5 pb-24 pt-40 lg:px-10">
        <FadeIn delay={0.15}>
          <p className="eyebrow" data-testid="hero-eyebrow">Global Industry Group</p>
        </FadeIn>
        <h1 className="mt-6 font-condensed text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-bold uppercase leading-[0.92] text-white">
          <MaskedLines
            lines={[
              "One Contractor.",
              "Multiple Disciplines.",
              <span key="r" className="text-gig-red">Built for Industry.</span>,
            ]}
          />
        </h1>
        <FadeIn delay={0.9}>
          <motion.div
            className="mt-7 h-[3px] w-28 origin-left bg-gig-red"
            initial={reduce ? false : { scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            aria-hidden="true"
          />
          <p className="mt-6 max-w-xl text-base md:text-lg leading-relaxed text-white/80" data-testid="hero-subcopy">
            Global Industry Group delivers integrated industrial construction and maintenance
            services with a focus on safety, quality, communication, and performance.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/services" className="btn-primary" data-testid="hero-explore-services">
              Explore Our Services <ArrowRight className="btn-arrow h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/contact" className="btn-outline-light" data-testid="hero-contact">
              Contact Us
            </Link>
          </div>
        </FadeIn>
      </motion.div>

    </section>
  );
}

function Intro() {
  return (
    <section className="bg-white" data-testid="home-intro">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-28">
        <Reveal>
          <p className="eyebrow">One Contractor. Multiple Disciplines.</p>
          <h2 className="mt-5 font-condensed text-4xl sm:text-5xl font-bold uppercase leading-[0.95] text-gig-black">
            Integrated Industrial Construction Built Around Your Facility
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-gig-steel">
            Global Industry Group is a multi-discipline industrial contractor delivering
            construction, maintenance, and specialty services for industrial, manufacturing,
            energy, and mission-critical facilities throughout Texas.
          </p>
          <p className="mt-4 text-base md:text-lg leading-relaxed text-gig-steel">
            Partner with one contractor capable of self-performing multiple trades — reducing
            project complexity while improving safety, communication, quality, coordination,
            and schedule performance.
          </p>
          <Link
            to="/about"
            className="mt-7 inline-flex items-center gap-2 font-condensed text-lg font-semibold uppercase tracking-[0.12em] text-gig-red hover:gap-3 transition-all"
            data-testid="intro-about-link"
          >
            About Global Industry Group <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <div className="relative">
            <img
              src={img("industrial-crew-plant-maintenance")}
              alt="Global Industry Group craftsmen performing mechanical work inside an operating industrial facility"
              className="w-full object-cover"
              loading="lazy"
              width="1448"
              height="1086"
            />
            <div className="absolute -bottom-8 -left-4 hidden w-2/5 border-4 border-white shadow-2xl sm:block md:-left-8" aria-hidden="true">
              <img
                src={img("industrial-process-piping-sunset")}
                alt=""
                className="w-full object-cover"
                loading="lazy"
                width="800"
                height="600"
              />
            </div>
            <div className="absolute -right-3 -top-3 h-16 w-16 border-r-4 border-t-4 border-gig-red" aria-hidden="true" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function WhyGlobal() {
  return (
    <section className="relative overflow-hidden bg-gig-navy" data-testid="home-why">
      <img src={img("refinery-night-blue")} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" loading="lazy" width="1600" height="900" />
      <div className="absolute inset-0 bg-gradient-to-b from-gig-navy/80 via-gig-navy/60 to-gig-navy/90" aria-hidden="true" />
      <div className="relative mx-auto max-w-[1440px] px-5 py-20 lg:px-10 lg:py-28">
        <Reveal>
          <p className="eyebrow">Why Global Industry Group</p>
          <h2 className="mt-5 max-w-2xl font-condensed text-4xl sm:text-5xl font-bold uppercase leading-[0.95] text-white">
            Built on Discipline. Driven by Performance.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_POINTS.map((point, i) => {
            const Icon = ICONS[point.icon];
            return (
              <Reveal key={point.title} delay={i * 0.1}>
                <div className="group h-full bg-gig-navy/60 p-7 backdrop-blur-sm transition-colors duration-500 hover:bg-gig-navy/40" data-testid={`why-${point.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="font-condensed text-sm font-bold tracking-[0.2em] text-gig-red">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Icon className="mt-5 h-8 w-8 text-gig-red" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="mt-4 font-condensed text-2xl font-bold uppercase text-white">{point.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/70">{point.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  return (
    <section className="relative overflow-hidden bg-gig-black" data-testid="home-services">
      <div className="absolute inset-0" aria-hidden="true">
        <img src={SITE.heroImage} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" loading="lazy" width="1600" height="900" />
        <div className="hero-blend-right absolute inset-0 hidden lg:block">
          <img src={SITE.dataCenterImage} alt="" className="h-full w-full object-cover opacity-60" loading="lazy" width="1600" height="900" />
        </div>
        <div className="absolute inset-0 bg-gig-black/70" />
      </div>
      <div className="relative mx-auto max-w-[1440px] px-5 py-20 lg:px-10 lg:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">What We Do</p>
            <h2 className="mt-5 font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-white">
              Our Services
            </h2>
          </div>
          <p className="max-w-md text-base text-white/70">
            Nine self-performed disciplines — from process piping to protective coatings —
            coordinated by one accountable team.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((service, i) => (
            <Reveal key={service.slug} delay={(i % 3) * 0.08}>
              <ServiceCard service={service} />
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 text-center">
          <Link to="/services" className="btn-primary" data-testid="services-view-all">
            View All Services <ArrowRight className="btn-arrow h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function IndustriesSection() {
  return (
    <section className="bg-gig-light" data-testid="home-industries">
      <div className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10 lg:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Markets</p>
            <h2 className="mt-5 font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-gig-black">
              Industries We Serve
            </h2>
          </div>
          <p className="max-w-md text-base text-gig-steel">
            We support complex facilities across traditional industry, infrastructure, energy,
            manufacturing, and rapidly growing mission-critical markets.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {INDUSTRIES.map((industry, i) => (
            <Reveal key={industry.slug} delay={(i % 4) * 0.06}>
              <IndustryCard industry={industry} featured={!!industry.featured} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function OneContractorAdvantage() {
  return (
    <section className="bg-white" data-testid="home-advantage">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-28">
        <Reveal className="relative order-2 lg:order-1">
          <img
            src={img("industrial-crew-rebar")}
            alt="Multiple trades working together within one industrial facility"
            className="w-full object-cover"
            loading="lazy"
            width="1600"
            height="1067"
          />
          <div className="absolute -bottom-3 -left-3 h-16 w-16 border-b-4 border-l-4 border-gig-red" aria-hidden="true" />
        </Reveal>
        <Reveal delay={0.1} className="order-1 lg:order-2">
          <p className="eyebrow">Why Global Industry Group</p>
          <h2 className="mt-5 font-condensed text-4xl sm:text-5xl font-bold uppercase leading-[0.95] text-gig-black">
            One Contractor. Multiple Disciplines.
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-gig-steel">
            Managing multiple contractors can create scheduling conflicts, communication gaps,
            duplicated management effort, and unnecessary project complexity. Global Industry
            Group simplifies project delivery by providing multiple services under one
            experienced team.
          </p>
          <motion.ul
            className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
          >
            {BENEFITS.map((b) => (
              <motion.li
                key={b}
                variants={{ hidden: { opacity: 0, x: -14 }, show: { opacity: 1, x: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } }}
                className="flex items-start gap-2.5 text-[15px] font-medium text-gig-black"
              >
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gig-red" strokeWidth={3} aria-hidden="true" />
                {b}
              </motion.li>
            ))}
          </motion.ul>
          <Link
            to="/about"
            className="mt-8 inline-flex items-center gap-2 font-condensed text-lg font-semibold uppercase tracking-[0.12em] text-gig-red hover:gap-3 transition-all"
            data-testid="advantage-why-choose-link"
          >
            Why Choose Global <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function ProjectSites() {
  return (
    <section className="bg-white border-t border-gig-steel/10" data-testid="home-project-sites">
      <div className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10 lg:py-28">
        <Reveal className="text-center">
          <p className="eyebrow justify-center">Proven Across Texas</p>
          <h2 className="mt-5 font-condensed text-4xl sm:text-5xl font-bold uppercase leading-[0.95] text-gig-black">
            Selected Project Sites Experience
          </h2>
        </Reveal>
        <div className="mt-10">
          <ClientsGrid />
        </div>
      </div>
    </section>
  );
}

function ProjectExperience() {
  return (
    <section className="bg-gig-navy" data-testid="home-project-experience">
      <div className="mx-auto max-w-[1440px] px-5 py-20 lg:px-10 lg:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <h2 className="max-w-2xl font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[0.95] text-white">
            Built on Trust. <span className="text-gig-red">Delivered with Pride.</span>
          </h2>
          <Link to="/projects" className="btn-outline-light" data-testid="projects-view-all">
            View Project Experience <ArrowRight className="btn-arrow h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {PROJECT_TYPES.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.06}>
              <Link
                to="/projects"
                data-testid={`project-type-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="card-zoom card-line group relative block overflow-hidden"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={p.image.replace(".webp", "-900.webp")} alt={p.imageAlt} className="zoom-img absolute inset-0 h-full w-full object-cover" loading="lazy" width="900" height="1200" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gig-black/95 via-gig-black/30 to-transparent" aria-hidden="true" />
                  <div className="absolute inset-x-0 bottom-0 p-5">
                    <h3 className="font-condensed text-xl font-bold uppercase leading-tight text-white">{p.name}</h3>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  return (
    <section className="bg-white" data-testid="home-safety">
      <div className="mx-auto grid max-w-[1440px] items-center gap-12 px-5 py-20 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-28">
        <Reveal>
          <p className="eyebrow">Safety & Quality</p>
          <h2 className="mt-5 font-condensed text-4xl sm:text-5xl font-bold uppercase leading-[0.95] text-gig-black">
            Built Right. <span className="text-gig-red">Built Safe.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-gig-steel">
            Every project is executed with detailed planning, experienced supervision, skilled
            craftsmen, and a commitment to protecting people, facilities, and operations.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {["ISNetworld®", "Avetta®", "DISA®", "Veriforce®", "AMPP"].map((name) => (
              <span key={name} className="border border-gig-steel/25 bg-gig-light px-4 py-2 font-condensed text-sm font-semibold uppercase tracking-[0.1em] text-gig-black">
                {name}
              </span>
            ))}
          </div>
          <Link
            to="/safety-quality"
            className="mt-8 inline-flex items-center gap-2 font-condensed text-lg font-semibold uppercase tracking-[0.12em] text-gig-red hover:gap-3 transition-all"
            data-testid="safety-commitment-link"
          >
            Our Safety Commitment <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <img
            src={img("electrician-ppe-work")}
            alt="Craftsman in proper PPE performing industrial electrical work"
            className="w-full object-cover"
            loading="lazy"
            width="1600"
            height="1067"
          />
          <div className="absolute -right-3 -top-3 h-16 w-16 border-r-4 border-t-4 border-gig-red" aria-hidden="true" />
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [organizationSchema, websiteSchema, breadcrumbSchema([{ name: "Home", path: "/" }])],
  };
  return (
    <>
      <Seo
        title="Industrial Contractor in Texas | Global Industry Group, Inc."
        description="Global Industry Group is a multi-discipline Texas industrial contractor for construction, maintenance, and specialty services — from refineries to data centers."
        path="/"
        jsonLd={jsonLd}
      />
      <Hero />
      <Intro />
      <Marquee />
      <WhyGlobal />
      <ServicesSection />
      <IndustriesSection />
      <OneContractorAdvantage />
      <ProjectSites />
      <ProjectExperience />
      <SafetySection />
      <CtaSection />
    </>
  );
}
